/**
 * Scene choices (spec 008): the limits and the shape check the editor and the
 * Service share. A scene has 0–4 options; an option's label is at most 120
 * characters; `ChoiceOption.id` is the routing key story edges point at.
 */

/** The most options a scene can have. */
export const MAX_CHOICES = 4;
/** The longest option label (characters). */
export const CHOICE_TEXT_MAX = 120;
/** The longest choice timer (seconds). */
export const CHOICE_TIMER_MAX_S = 120;

const SETTINGS_KEYS = ['layout', 'position', 'timer', 'shuffleOptions', 'showBeforeEnd'] as const;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Checks `choices` (absent or null is fine; else an array of ≤ 4 options with
 * unique non-empty string ids, string text of ≤ 120 characters and an
 * optional integer mediaId). Returns an error naming the field, or null.
 */
export function validateChoices(choices: unknown): string | null {
  if (choices === undefined || choices === null) return null;
  if (!Array.isArray(choices)) return 'choices must be an array';
  if (choices.length > MAX_CHOICES) return `choices must have at most ${MAX_CHOICES} options`;
  const seen = new Set<string>();
  for (let i = 0; i < choices.length; i++) {
    const c = choices[i];
    const at = `choices[${i}]`;
    if (!isPlainObject(c)) return `${at} must be an object`;
    if (typeof c.id !== 'string' || c.id.trim() === '') return `${at}.id must be a non-empty string`;
    if (seen.has(c.id)) return `${at}.id "${c.id}" is a duplicate`;
    seen.add(c.id);
    if (typeof c.text !== 'string') return `${at}.text must be a string`;
    if (c.text.length > CHOICE_TEXT_MAX) return `${at}.text must be at most ${CHOICE_TEXT_MAX} characters`;
    if (c.mediaId !== undefined && c.mediaId !== null && !Number.isInteger(c.mediaId)) {
      return `${at}.mediaId must be an integer`;
    }
  }
  return null;
}

/**
 * Checks `choiceSettings` (absent or null is fine; else an object with only
 * the known keys: layout list|grid, position bottom|center, timer an integer
 * 0–120 (0 = no timer), shuffleOptions boolean, showBeforeEnd a number ≥ 0).
 */
export function validateChoiceSettings(settings: unknown): string | null {
  if (settings === undefined || settings === null) return null;
  if (!isPlainObject(settings)) return 'choiceSettings must be an object';
  for (const key of Object.keys(settings)) {
    if (!(SETTINGS_KEYS as readonly string[]).includes(key)) return `choiceSettings.${key} is not a known setting`;
  }
  const { layout, position, timer, shuffleOptions, showBeforeEnd } = settings;
  const given = (v: unknown) => v !== undefined && v !== null;
  if (given(layout) && layout !== 'list' && layout !== 'grid') return 'choiceSettings.layout must be list or grid';
  if (given(position) && position !== 'bottom' && position !== 'center') {
    return 'choiceSettings.position must be bottom or center';
  }
  if (given(timer) && (!Number.isInteger(timer) || (timer as number) < 0 || (timer as number) > CHOICE_TIMER_MAX_S)) {
    return `choiceSettings.timer must be a whole number 0–${CHOICE_TIMER_MAX_S}`;
  }
  if (given(shuffleOptions) && typeof shuffleOptions !== 'boolean') return 'choiceSettings.shuffleOptions must be a boolean';
  if (given(showBeforeEnd) && (typeof showBeforeEnd !== 'number' || !Number.isFinite(showBeforeEnd) || showBeforeEnd < 0)) {
    return 'choiceSettings.showBeforeEnd must be a number ≥ 0';
  }
  return null;
}
